import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthSelectors,
  DateUtils,
  ELanguage,
  LanguageSelectors,
  ProgressSelectors,
  RemoteConfigService,
  StatsActions,
  StatsSelectors,
  logger,
  preloadStatsSaga,
  waitForFirebaseReadySaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { getDayOfWeekFromNow } from '../base/dateUtils';
import ENV from '../base/env';
import { CyclesSelectors } from '../cycles';
import { SessionSelectors } from '../session';
import {
  APP_VERSION,
  DEFAULT_WEB_RATING_REQUEST_CONFIG,
  FEEDBACK_EMAIL,
  FEEDBACK_STAT,
  POSITIVE_FEEDBACK_URLS,
} from './constants';
import { trackFeedbackResponse } from './firebaseActions';
import { sendUserFeedback } from './service';
import { FeedbackActions } from './slice';
import { EFeedbackType, ISendFeedbackPayload, IWebRatingRequestConfig } from './types';
import { detectOSNameFromUA } from './utils';

export function* checkFeedbackStatusSaga() {
  // Ensure that stats are loaded
  yield call(preloadStatsSaga);
  // Ensure that firebse is ready
  yield call(waitForFirebaseReadySaga);

  // Remote config data
  const webRatingRequestConfig: IWebRatingRequestConfig | undefined = yield call(
    RemoteConfigService.getObjectConfigValue,
    ENV.WebRatingRequestConfig,
    DEFAULT_WEB_RATING_REQUEST_CONFIG
  );
  // lets keep this debug for now as there was reports that config was probably not loaded in some cases
  yield call(logger, ['webRatingRequestConfig', webRatingRequestConfig]);

  if (!webRatingRequestConfig?.enabled) {
    yield put(FeedbackActions.setShowFeedbackDialog(false));
    return;
  }

  // Timestamp of previous feedback from stats
  const prevFeedbackTimestamp: number | undefined = yield select(StatsSelectors.getStatIntValueById, FEEDBACK_STAT);

  const daysFromPrevFeedback = yield call(getDayOfWeekFromNow, prevFeedbackTimestamp ?? 0);
  const prevFeedbackType: string | undefined = yield select(StatsSelectors.getStatStringValueById, FEEDBACK_STAT);

  let daysLimit = 0;
  switch (prevFeedbackType) {
    case EFeedbackType.later:
      daysLimit = webRatingRequestConfig.days_after_later ?? -1;
      break;
    case EFeedbackType.negative:
      daysLimit = webRatingRequestConfig.days_after_negative ?? -1;
      break;
    case EFeedbackType.positive:
      daysLimit = webRatingRequestConfig.days_after_positive ?? -1;
      break;
  }

  if (daysLimit < 0 || daysFromPrevFeedback <= daysLimit) {
    // if days limit set to -1 or defined but not passed - do not show the popup
    yield put(FeedbackActions.setShowFeedbackDialog(false));
    return;
  }

  const afterNumWorkoutsCofig = webRatingRequestConfig.after_num_workouts;
  const onMinAccuracyCofig = webRatingRequestConfig.on_min_accuracy;
  // Last completed workoutId
  const workoutId: number = yield select(SessionSelectors.getLastCompletedWorkoutId);
  // make sure that full progress is loaded
  yield call(waitForFullProgressSaga);
  const accuracy: number = workoutId ? yield select(ProgressSelectors.getLastWorkoutAccuracy, workoutId) : 0;
  // Number of workouts user done previously
  const numWorkoutsDone: number = yield select(ProgressSelectors.getTotalCompletionCount);

  if (accuracy < onMinAccuracyCofig || numWorkoutsDone < afterNumWorkoutsCofig) {
    yield put(FeedbackActions.setShowFeedbackDialog(false));
    return;
  }

  // Show feedback dialog
  yield put(FeedbackActions.setShowFeedbackDialog(true));
}

export function* feedbackStatsSavedSaga(feedbackType: EFeedbackType) {
  // Stats need timestamp in seconds
  const timestamp = yield call(DateUtils.getUnixTimestamp);

  yield put(
    StatsActions.updateGenericStat(FEEDBACK_STAT, {
      valueInt: timestamp,
      valueTxt: feedbackType,
    })
  );
  yield put(StatsActions.saveStatsQueue());

  const statsSave = yield take([StatsActions.saveStatsQueueSuccess, StatsActions.saveStatsQueueFailed]);

  return statsSave.type === StatsActions.saveStatsQueueSuccess.type;
}

export function* sendFeedbackSaga(action: PayloadAction<ISendFeedbackPayload>) {
  const { text: message, rating } = action.payload;
  // track users response
  yield put(trackFeedbackResponse(rating));
  // If user choose Later then save stats
  if (rating === EFeedbackType.later) {
    const areStatsSaved: boolean = yield call(feedbackStatsSavedSaga, EFeedbackType.later);

    if (!areStatsSaved) {
      yield put(FeedbackActions.setShowFeedbackDialog(true));

      throw new Error('Something went wrong. Please try again.');
    }

    yield put(FeedbackActions.setShowFeedbackDialog(false));

    return;
  }

  // If user сhoose to leave feedback on trustpilot then save stats and redirect to trustpilot
  if (rating === EFeedbackType.positive) {
    const areStatsSaved: boolean = yield call(feedbackStatsSavedSaga, EFeedbackType.positive);

    if (areStatsSaved) {
      yield put(FeedbackActions.setShowFeedbackDialog(false));

      const lang: ELanguage = yield select(LanguageSelectors.getLanguage);

      window.open(POSITIVE_FEEDBACK_URLS[lang], '_blank');

      return;
    }

    yield put(FeedbackActions.setShowFeedbackDialog(true));

    throw new Error('Something went wrong. Please try again.');
  }

  const recipient = FEEDBACK_EMAIL;
  const currentCycle: number = yield select(CyclesSelectors.getCurrentCycleId);
  const currentSession: number = yield select(CyclesSelectors.getCurrentSessionId);
  const userName: string = yield select(AuthSelectors.getUserName) || '';
  const userId: number = yield select(AuthSelectors.getUserId);
  // Requirements: must be a string "True" or "False" for better readability by CC
  const isPremiumFromSelector: boolean = yield select(AuthSelectors.isPremium);
  const isPremium = isPremiumFromSelector ? 'True' : 'False';
  const language: ELanguage = yield select(LanguageSelectors.getLanguage);
  const userAgent = navigator?.userAgent || '';
  const osName: string = yield call(detectOSNameFromUA, userAgent);
  const workoutId: number = yield select(SessionSelectors.getLastCompletedWorkoutId);
  const exerciseName: string = yield select(SessionSelectors.getExerciseName, workoutId);
  const subject = `Braintraining User Feedback | ${osName} | ${APP_VERSION} | ${userId}`;

  try {
    yield call(sendUserFeedback, {
      subject,
      recipient,
      currentCycle,
      currentSession,
      userId,
      userName,
      isPremium,
      language,
      osName,
      userAgent,
      message,
      rating,
      lastGameplay: exerciseName,
    });
    yield put(FeedbackActions.sendFeedbackSuccess());

    // If user choose to write us directly then save stats, prepare all data and send feedback to CC
    const areStatsSaved: boolean = yield call(feedbackStatsSavedSaga, EFeedbackType.negative);

    if (!areStatsSaved) {
      yield put(FeedbackActions.setShowFeedbackDialog(true));

      throw new Error('Something went wrong. Please try again.');
    }

    yield put(FeedbackActions.setShowFeedbackDialog(false));
  } catch {
    yield put(FeedbackActions.sendFeedbackFailure());
  }
}

export default function* watchFeedback() {
  yield all([
    takeLatest(FeedbackActions.checkFeedbackStatus, checkFeedbackStatusSaga),
    takeEvery(FeedbackActions.sendFeedback, sendFeedbackSaga),
  ]);
}
